import { useEffect } from 'react';

export function useScripts() {
  useEffect(() => {
    // Don't add a global error handler - let React handle errors naturally
    // Script loading errors are handled in loadScript's onerror callback
    
    // Load jQuery and other scripts
    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        // Check if script already exists
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = (error) => {
          // Log error but don't break the app
          console.warn(`Failed to load script: ${src}`, error);
          resolve(); // Resolve instead of reject to continue loading other scripts
        };
        document.body.appendChild(script);
      });
    };

    const initializeScripts = async () => {
      try {
        // Load jQuery first
        await loadScript('/assets/js/lib/jquery.js');
        
        // Wait for jQuery to be available
        let retries = 0;
        while (!window.jQuery && retries < 10) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }
        
        if (!window.jQuery) {
          console.warn('jQuery failed to load, some features may not work');
        }
        
        // Load other scripts in order - wrap each in try-catch to prevent one failure from stopping others
        const scripts = [
          'https://stackpath.bootstrapcdn.com/bootstrap/4.1.1/js/bootstrap.min.js',
          '/assets/js/lib/owl.carousel.min.js',
          '/assets/js/lib/css3-animate-it.js',
          '/assets/js/lib/counter.js',
          'https://cdnjs.cloudflare.com/ajax/libs/moment.js/2.22.0/moment.min.js',
          '/vendor/vex/js/vex.combined.min.js',
          'https://cdnjs.cloudflare.com/ajax/libs/card/2.4.0/card.min.js',
          'https://cdnjs.cloudflare.com/ajax/libs/bootstrap-select/1.12.4/js/bootstrap-select.min.js',
          '/assets/js/chosen_v1.8.7/chosen.jquery.min.js',
          '/assets/js/main.js',
          '/assets/js/next.js?v=2',
          '/assets/js/validatecc.js',
          '/assets/js/axn.js'
        ];
        
        // Load scripts sequentially but continue even if one fails
        for (const src of scripts) {
          try {
            await loadScript(src);
            // Small delay between script loads to avoid overwhelming the browser
            await new Promise(resolve => setTimeout(resolve, 50));
          } catch (error) {
            console.warn(`Error loading script ${src}:`, error);
            // Continue loading other scripts
          }
        }

        // Wait a bit for DOM to be ready and initialize animations
        setTimeout(() => {
          try {
            // The css3-animate-it library initializes on scroll
            // We need to trigger it manually on page load
            if (window.jQuery) {
              // Check if the animation library is loaded
              const checkAndInit = () => {
                try {
                  // Force a scroll event to trigger animations
                  const scrollEvent = new Event('scroll', { bubbles: true });
                  window.dispatchEvent(scrollEvent);
                  
                  // Also try to manually trigger animations for visible elements
                  const animatedElements = document.querySelectorAll('.animated');
                  animatedElements.forEach((el) => {
                    try {
                      // Check if element is in viewport
                      const rect = el.getBoundingClientRect();
                      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
                      
                      if (isVisible) {
                        // Remove and re-add animated class to trigger animation
                        const classes = el.className;
                        el.className = classes.replace('animated', '').trim();
                        setTimeout(() => {
                          el.className = classes;
                        }, 10);
                      }
                    } catch (e) {
                      console.warn('Error processing animated element:', e);
                    }
                  });
                } catch (e) {
                  console.warn('Error in checkAndInit:', e);
                }
              };
              
              // Try multiple times to ensure animations trigger
              checkAndInit();
              setTimeout(checkAndInit, 100);
              setTimeout(checkAndInit, 300);
            }
          } catch (error) {
            console.warn('Error initializing animations:', error);
          }
        }, 300);
      } catch (error) {
        console.error('Error in initializeScripts:', error);
        // Don't throw - allow the app to continue even if scripts fail
      }
    };

    initializeScripts();
    
    // No cleanup needed - scripts remain loaded
  }, []);
}
